import { PromotionType } from '../../graphQL/commons/types'
import {
  IConditionAction,
  IConfig,
  IPromotionShortcut,
  IPromotionsAvailability,
  IPromotionUiCatalog,
} from '../../interfaces'
import { GetPromotionsUiDetails, IProduct } from '../../interfaces/catalog'
import { getComboPromotionUiDetails } from './getComboPromotionUiDetails'
import { getPromotionFields } from '../common/getPromotionFields'
import { getComplexPromotionUiDetails } from './getComplexPromotionUiDetails'
import { getDirectPromotionUiDetails } from './getDirectPromotionUiDetails'
import { getVolumePromotionUiDetails } from './getVolumePromotionUiDetails'
import {
  applyAddAllAvailability,
  isDisableUnfulfillableAddAllOn,
} from '../promotions/addAllAvailability'

export interface ISetPromotionShortcutsArgs {
  product: IProduct
  config?: IConfig
  includeAllPromotions: boolean
  /**
   * The stock lookup's verdict on each promotion's add-all button. Read only
   * under `globals.promotions.disableUnfulfillableAddAll`, and only when it
   * was computed for `product`; absent leaves every button as it is.
   */
  promotionsAvailability?: IPromotionsAvailability
}

const comboComplexPromotionsTypes = [PromotionType.COMPLEX, PromotionType.COMBO]
const promotionsWithoutShortcuts = [PromotionType.DIRECT]

const allPromotionsTypes = [
  PromotionType.COMPLEX,
  PromotionType.COMBO,
  PromotionType.DIRECT,
  PromotionType.VOLUME,
]

const getPromotionDetailsArray: {
  condition: (type: PromotionType) => boolean
  transformAction: GetPromotionsUiDetails
}[] = [
  {
    condition: (type) => type === PromotionType.COMPLEX,
    transformAction: getComplexPromotionUiDetails,
  },
  {
    condition: (type) => type === PromotionType.COMBO,
    transformAction: getComboPromotionUiDetails,
  },
  {
    condition: (type) => type === PromotionType.DIRECT,
    transformAction: getDirectPromotionUiDetails,
  },
  {
    condition: (type) => type === PromotionType.VOLUME,
    transformAction: getVolumePromotionUiDetails,
  },
]

interface IPromotionDetailsTransformerArgs
  extends Pick<ISetPromotionShortcutsArgs, 'product' | 'includeAllPromotions'> {
  promotionDetails?: IPromotionUiCatalog
  disableUnfulfillableAddAll: boolean
}

const promotionDetailsTransformer: IConditionAction<
  IPromotionDetailsTransformerArgs,
  IPromotionShortcut[]
>[] = [
  {
    condition: ({ promotionDetails }) =>
      Number(promotionDetails?.data?.customerBuys?.groups?.length) > 1 &&
      !!promotionDetails?.data,
    transformAction: ({
      promotionDetails,
      product,
      disableUnfulfillableAddAll,
    }) => {
      const { title = '', description, data } = promotionDetails!
      const { customerBuysGroups, customerGetsGroups } = getPromotionFields(
        data!,
      )
      // With several gets groups too, a group's button has no single set of
      // lines to send — with the flag off a click sends an empty request.
      const showButton = !(
        disableUnfulfillableAddAll && customerGetsGroups.length > 1
      )

      const result = customerBuysGroups.map<IPromotionShortcut>((group) => {
        const suffix = group.items.reduce<string>(
          (acc, { sku, quantity }, index) => {
            const isFirst = index === 0
            const prefix = isFirst ? '' : ', '
            if (sku === product.sku) {
              return acc.concat(`${prefix}(${quantity}) ${product.name}`)
            } else {
              return acc.concat(`${prefix}(${quantity}) ${sku}`)
            }
          },
          '',
        )

        return {
          id: group.identifier || '',
          title,
          description: `${description} - ${suffix}.`,
          data: {
            promotion: data,
            groupIdentifier: group.identifier,
          },
          showButton,
        }
      })
      return result
    },
  },
  {
    condition: ({ promotionDetails }) =>
      promotionDetails !== undefined &&
      Number(promotionDetails?.data?.customerGets?.groups?.length) > 1,
    transformAction: ({ promotionDetails }) => [
      {
        id: promotionDetails?.data?.id || '',
        title: promotionDetails?.title || '',
        description: promotionDetails?.description || '',
        data: { promotion: promotionDetails!.data },
        showButton: false,
      },
    ],
  },
  {
    condition: ({ promotionDetails }) =>
      promotionDetails !== undefined &&
      comboComplexPromotionsTypes.some(
        (type) => type === promotionDetails.data?.type,
      ),
    transformAction: ({ promotionDetails }) => [
      {
        id: promotionDetails?.data?.id || '',
        title: promotionDetails?.title || '',
        description: promotionDetails?.description || '',
        data: { promotion: promotionDetails!.data },
        showButton: true,
      },
    ],
  },
  {
    condition: ({ promotionDetails }) =>
      promotionDetails !== undefined &&
      allPromotionsTypes.some((type) => type === promotionDetails.data?.type),
    transformAction: ({ promotionDetails }) => [
      {
        id: promotionDetails?.data?.id || '',
        title: promotionDetails?.title || '',
        description: promotionDetails?.description || '',
        data: { promotion: promotionDetails!.data },
        showButton: false,
      },
    ],
  },
]

export const getPromotionsShortcuts = ({
  product,
  config,
  includeAllPromotions,
  promotionsAvailability,
}: ISetPromotionShortcutsArgs): IPromotionShortcut[] => {
  if (product.promotions?.length && config) {
    const { promotions } = product
    const disableUnfulfillableAddAll = isDisableUnfulfillableAddAllOn(config)

    const promotionsToBeDisplayed = includeAllPromotions
      ? promotions
      : [promotions[0]]

    if (
      !includeAllPromotions &&
      ![...comboComplexPromotionsTypes, ...promotionsWithoutShortcuts].some(
        (type) => type === promotionsToBeDisplayed[0]?.type,
      )
    ) {
      return []
    }

    const promotionsUiDetails = promotionsToBeDisplayed
      .map((promotion) => {
        return getPromotionDetailsArray
          .find(({ condition }) => condition(promotion.type))
          ?.transformAction({
            promotion,
            config,
          })
      })
      .filter((item): item is IPromotionUiCatalog => item !== undefined)

    const promotionsShortcuts = promotionsUiDetails.reduce<
      IPromotionShortcut[]
    >((acc, promotionDetails) => {
      const argsCondition = {
        promotionDetails,
        product,
        includeAllPromotions,
        disableUnfulfillableAddAll,
      }
      const validConditionAction = promotionDetailsTransformer.find(
        ({ condition }) => condition(argsCondition),
      )
      if (validConditionAction) {
        return [...acc, ...validConditionAction.transformAction(argsCondition)]
      }
      return acc
    }, [])

    if (!disableUnfulfillableAddAll) return promotionsShortcuts

    return promotionsShortcuts.map((shortcut) =>
      applyAddAllAvailability(shortcut, {
        availability: promotionsAvailability,
        sku: product.sku,
        config,
      }),
    )
  }

  return []
}

export default getPromotionsShortcuts
